import { HealthStatus } from "core/services/copilotService";

export type ConfigState = "error" | "misconfigured" | "healthy" | "unconfigured" | "loading";

interface ConfigStateInput {
    isConfigured: boolean | null;
    healthStatus: HealthStatus | null;
    isLoading: boolean;
    isInitEndpointAccessible: boolean;
}

/**
 * Determines the configuration state based on:
 * - Whether /do/init endpoint is accessible (determines ERROR vs other states)
 * - Health status of the configured server
 * - Whether the system is still loading
 *
 * Single source of truth for state determination logic.
 * Easy to modify behavior across the entire app by updating this function.
 */
export const getConfigState = (input: ConfigStateInput): ConfigState => {
    const { isConfigured, healthStatus, isLoading, isInitEndpointAccessible } = input;

    if (isLoading) {
        return "loading";
    }

    if (!isConfigured) {
        return "unconfigured";
    }

    if (!isInitEndpointAccessible) {
        return "error";
    }

    if (healthStatus?.status === "degraded") {
        return "misconfigured";
    }

    if (healthStatus?.status === "healthy") {
        return "healthy";
    }

    if (healthStatus && healthStatus.status !== "healthy") {
        return "misconfigured";
    }

    return "misconfigured";
};

/**
 * Extracts the booleans needed by UI components from the config state.
 * Use this in components that still expect the old prop-based API.
 */
export const configStateToProps = (
    state: ConfigState
): {
    isHealthy: boolean;
    isMisconfigured: boolean;
    isError: boolean;
} => {
    return {
        isHealthy: state === "healthy",
        isMisconfigured: state === "misconfigured",
        isError: state === "error"
    };
};
